/**
 * Bogue 1202 — Panneau « Erreurs de passerelle » (Eric 2026-06-26).
 *
 * Eric voit en production « The origin web server returned an invalid or
 * incomplete response to Cloudflare » et ne peut pas consulter les logs du
 * conteneur déployé. Ce panneau affiche ce que le NAVIGATEUR a constaté :
 * route exacte, code (520 ≠ 502 ≠ 524), durée avant l'échec et identifiant
 * Cloudflare. Il suffit de reproduire le bogue une fois, puis de venir lire
 * ici — ou de m'envoyer les lignes.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowClockwise, PlugsConnected } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return String(iso);
  }
};

export const GatewayErrorsPanel = ({ initial , awaiting }) => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/sentinel/gateway-errors?limit=50`, {
        withCredentials: true,
      });
      setData(r.data);
    } catch {
      setHidden(true);
    }
  }, []);

    // Tâche 1249 — Si la page a déjà tout reçu dans l'appel groupé, la carte
  // ne refait PAS son propre appel : la page passait 23 appels par affichage.
  useEffect(() => {
    if (initial) {
      setData(initial);
      return;
    }
    // Tâche 1249 — L'appel groupé est en route : ne pas en lancer un second.
    if (awaiting) return;
    load();
  }, [load, initial, awaiting]);

  if (hidden) return null;

  const reports = data?.reports || [];
  const byRoute = data?.by_route || [];

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-5"
      data-testid="gateway-errors-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
            <PlugsConnected size={18} weight="duotone" /> Erreurs de passerelle (Cloudflare)
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-3xl">
            Quand le serveur d'origine ne répond pas, il n'a rien à journaliser : seul le
            navigateur sait quelle requête a échoué. Chaque erreur 502/503/504/52x est donc
            enregistrée ici, avec sa durée — <strong>immédiate</strong> pointe un plantage du
            serveur, <strong>≈100 s</strong> un dépassement de délai Cloudflare. Conservé
            14 jours.
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-2 text-sm rounded-lg border border-zinc-300 hover:bg-zinc-50 transition-colors"
          data-testid="gateway-errors-refresh-btn"
          aria-label="Rafraîchir"
        >
          <ArrowClockwise size={16} />
        </button>
      </div>

      {reports.length === 0 ? (
        <p
          className="mt-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
          data-testid="gateway-errors-empty"
        >
          Aucune erreur de passerelle enregistrée. Si le bogue se reproduit, la requête
          fautive apparaîtra ici automatiquement.
        </p>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="gateway-errors-by-route-table">
              <thead>
                <tr className="text-left text-zinc-500 border-b border-zinc-200">
                  <th className="py-2 pr-3 font-medium">Route</th>
                  <th className="py-2 pr-3 font-medium">Occurrences</th>
                  <th className="py-2 pr-3 font-medium hidden sm:table-cell">Codes</th>
                  <th className="py-2 font-medium whitespace-nowrap">Durée max</th>
                </tr>
              </thead>
              <tbody>
                {byRoute.map((g) => (
                  <tr key={g.route} className="border-b border-zinc-100 last:border-0">
                    <td className="py-2 pr-3 font-mono text-xs break-all">{g.route}</td>
                    <td className="py-2 pr-3 font-semibold text-rose-700">{g.count}</td>
                    <td className="py-2 pr-3 text-xs hidden sm:table-cell">
                      {Object.entries(g.statuses || {})
                        .map(([s, n]) => `${s} ×${n}`)
                        .join(", ")}
                    </td>
                    <td className="py-2 text-xs">{g.max_duration_ms} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="gateway-errors-table">
              <thead>
                <tr className="text-left text-zinc-500 border-b border-zinc-200">
                  <th className="py-2 pr-3 font-medium">Quand</th>
                  <th className="py-2 pr-3 font-medium">Requête</th>
                  <th className="py-2 pr-3 font-medium">Code</th>
                  <th className="py-2 pr-3 font-medium whitespace-nowrap">Durée</th>
                  <th className="py-2 font-medium hidden sm:table-cell">cf-ray</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r, i) => (
                  <tr key={`${r.detected_at}-${i}`} className="border-b border-zinc-100 last:border-0">
                    <td className="py-2 pr-3 text-xs whitespace-nowrap">{fmt(r.detected_at)}</td>
                    <td className="py-2 pr-3 font-mono text-xs break-all">
                      {(r.method || "").toUpperCase()} {r.url}
                    </td>
                    <td className="py-2 pr-3 font-semibold text-rose-700">
                      {r.status}
                      {r.cf_error_code && r.cf_error_code !== String(r.status)
                        ? ` (CF ${r.cf_error_code})`
                        : ""}
                    </td>
                    <td className="py-2 pr-3 text-xs">{r.duration_ms} ms</td>
                    <td className="py-2 text-xs font-mono hidden sm:table-cell">
                      {r.cf_ray || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

export default GatewayErrorsPanel;
